import React from 'react';
import { Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Pricing = () => {
  const navigate = useNavigate();

  const handlePlanSelect = (plan: string) => {
    navigate(`/signup?plan=${encodeURIComponent(plan)}`);
  };

  const plans = [
    {
      name: 'Monthly Plan',
      price: '৳50',
      period: '/month',
      billing: 'Billed monthly',
      features: [
        'Weekly Email Newsletter',
        'VIP Portal Access on Website',
        'Community Support',
        'Discussion & Guidelines',
        'Connection with Successful People'
      ],
      cta: 'Get Started',
      value: '1 Month',
      highlight: false,
      buttonColor: 'gray'
    },
    {
      name: '6-Month Plan',
      price: '৳250',
      period: '/6 months',
      billing: 'Billed every 6 months',
      originalPrice: '৳300',
      savings: 'Save ৳50',
      features: [
        'Weekly Email Newsletter',
        'VIP Portal Access on Website',
        'Community Support',
        'Discussion & Guidelines',
        'Connection with Successful People',
        '1× One-to-One Application Review for 1 Application'
      ],
      cta: 'Get Started',
      value: '6 Months',
      highlight: true,
      badge: 'BEST VALUE',
      buttonColor: 'red'
    },
    {
      name: '1-Year Plan',
      price: '৳500',
      period: '/year',
      billing: 'Billed yearly',
      originalPrice: '৳600',
      savings: 'Save ৳100',
      features: [
        'Weekly Email Newsletter',
        'VIP Portal Access on Website',
        'Community Support',
        'Discussion & Guidelines',
        'Connection with Successful People',
        '12× One-to-One Application Review for 1 Application'
      ],
      cta: 'Get Started',
      value: '1 Year',
      highlight: false,
      badge: 'PREMIUM',
      buttonColor: 'gray'
    }
  ];

  return (
    <section className="py-20 bg-rich-black relative overflow-hidden" id="pricing">
      {/* Background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-vibrant-red/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-crimson/5 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
            Choose Your Plan
          </h2>
          <p className="text-xl text-medium-gray max-w-2xl mx-auto">
            Invest in your future. Choose the plan that fits you.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <div 
              key={index}
              className={`relative rounded-2xl p-8 transition-all duration-300 hover:-translate-y-2 flex flex-col ${
                plan.highlight 
                  ? 'bg-dark-charcoal border-2 border-vibrant-red shadow-[0_0_30px_rgba(220,20,60,0.3)]' 
                  : 'bg-dark-charcoal border border-white/10 hover:border-white/20'
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className={`px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-white ${
                    plan.badge === 'BEST VALUE' ? 'bg-vibrant-red' : 'bg-yellow-500 text-black'
                  }`}>
                    {plan.badge}
                  </span>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                <div className="flex items-center justify-center gap-2 mb-2">
                  {plan.originalPrice && (
                    <span className="text-medium-gray line-through text-lg">{plan.originalPrice}</span>
                  )}
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-extrabold text-white">{plan.price}</span>
                    <span className="text-medium-gray ml-1">{plan.period}</span>
                  </div>
                </div>
                {plan.savings && (
                  <span className="inline-block text-green-400 text-sm font-bold mb-2">
                    {plan.savings}
                  </span>
                )}
                <p className="text-sm text-medium-gray mt-2">{plan.billing}</p>
              </div>

              <ul className="space-y-4 mb-8 flex-grow">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <Check className="h-5 w-5 text-vibrant-red mr-3 flex-shrink-0 mt-0.5" />
                    <span className={`text-sm ${feature.includes('12×') ? 'text-white font-bold' : 'text-light-gray'}`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handlePlanSelect(plan.value)}
                className={`w-full py-3 px-6 rounded-lg font-bold text-sm transition-all duration-200 mt-auto ${
                  plan.buttonColor === 'red'
                    ? 'bg-vibrant-red text-white hover:bg-crimson hover:shadow-glow transform hover:scale-105'
                    : 'bg-dark-gray text-white hover:bg-white/20 border border-white/10'
                }`}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>
        
        <div className="mt-12 text-center">
          <p className="text-medium-gray text-sm">
            Secure payment via bKash, Nagad, or Card. 
            <span className="block sm:inline sm:ml-2">30-day money-back guarantee.</span>
          </p>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
